/**
 * The publication's graphical abstract, on the Abstract tab: one picture that sums up the paper.
 * With AI enabled it can be made from the whole paper (title, abstract, keywords, every section), paid in
 * Otable credits or with the person's own OpenAI key (called from the browser, like the writing assistant).
 * The image is compressed like any figure and counts towards the 5 MB of storage. One can also upload a picture.
 * Backend: /proposal/proposals/<id>/graphical_abstract/ (graphical_abstract.py).
 */
import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  AutoAwesome as AIIcon, DeleteOutline as RemoveIcon, Download as DownloadIcon, Upload as UploadIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useUserContext } from 'shared/contexts/UserContext';
import * as byokVault from 'shared/utils/byokVault';

const SOURCE_KEY = 'otable.ai.source';           // shared with the AI writing assistant
const readSource = () => { try { return localStorage.getItem(SOURCE_KEY) || 'credits'; } catch { return 'credits'; } };

export default function GraphicalAbstractPanel({ proposal, aiEnabled, figureData, setFigureData }) {
  const { user } = useUserContext();
  const [source, setSource] = useState(readSource);
  const [busy, setBusy] = useState('');          // 'generating' | 'uploading' | 'removing'
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const inputRef = useRef(null);
  const base = `/proposal/proposals/${proposal.id}`;
  const figure = (figureData.figures || []).find((f) => f.id === figureData.graphical_abstract_id);
  const vault = user ? byokVault.status(user.id) : { unlocked: false, provider: null };
  const ownKey = source === 'own';

  if (!figure && !aiEnabled) return null;

  const changeSource = (_e, value) => {
    if (!value) return;
    setSource(value);
    try { localStorage.setItem(SOURCE_KEY, value); } catch { /* ignore */ }
  };

  const adoptUpload = async (file) => {
    const form = new FormData();
    form.append('image', file, file.name || 'graphical-abstract.png');
    const up = await axiosService.post(`${base}/figures/`, form, { timeout: 120000 });
    const res = await axiosService.post(`${base}/graphical_abstract/`, { figure_id: up.data.figure.id });
    setFigureData(res.data);
  };

  const run = async (kind, work) => {
    setBusy(kind); setError(''); setNote('');
    try { await work(); } catch (e) { setError(e.response?.data?.error || e.message || 'Something went wrong.'); }
    setBusy('');
  };

  const generate = () => run('generating', async () => {
    if (ownKey) {
      const { data } = await axiosService.get(`${base}/graphical_abstract/`);
      const blob = await byokVault.generateImage(user.id, data);
      await adoptUpload(new File([blob], 'graphical-abstract.png', { type: 'image/png' }));
      setNote('Made with your own OpenAI key.');
    } else {
      const { data } = await axiosService.post(`${base}/graphical_abstract/`, { generate: true }, { timeout: 240000 });
      setFigureData(data);
      setNote(data.credits_used ? `${data.credits_used} credit${data.credits_used === 1 ? '' : 's'} used.` : '');
    }
  });

  const remove = () => run('removing', async () => {
    const { data } = await axiosService.delete(`${base}/graphical_abstract/`);
    setFigureData(data);
  });

  const ownKeyProblem = ownKey && (!vault.unlocked
    ? 'Unlock your API key in the AI writing assistant first.'
    : vault.provider !== 'openai' ? 'Claude cannot make images: use Otable credits or an OpenAI key.' : '');

  return (
    <Paper variant="outlined" sx={{ p: 2, mt:2, mb: 2, borderRadius: 1 }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>Graphical abstract</Typography>
        {aiEnabled && (
          <ToggleButtonGroup size="small" exclusive value={source} onChange={changeSource} disabled={Boolean(busy)}>
            <ToggleButton value="credits" sx={{ textTransform: 'none', fontSize: 12.5, py: 0.25 }}>Otable credits</ToggleButton>
            <ToggleButton value="own" sx={{ textTransform: 'none', fontSize: 12.5, py: 0.25 }}>My own key</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {figure ? (
        <Box component="img" src={figure.url} alt={`Graphical abstract of ${proposal.title}`}
          sx={{ display: 'block', maxWidth: '100%', maxHeight: 380, objectFit: 'contain', mx: 'auto', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
      ) : (
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
          One picture that sums up your paper for the journal page. AI draws it from the title, the abstract, the
          keywords and every section, so it is best made once the text is in good shape. Check every label before
          you submit it.
        </Typography>
      )}

      {busy === 'generating' && (
        <Box display="flex" alignItems="center" gap={1} mt={1.5}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Drawing the picture; this takes up to a minute…</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      {ownKeyProblem && !busy && <Alert severity="info" sx={{ mt: 1.5 }}>{ownKeyProblem}</Alert>}
      {note && !error && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>{note}</Typography>}

      <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
        {aiEnabled && (
          <Button size="small" variant={figure ? 'outlined' : 'contained'} startIcon={<AIIcon />}
            disabled={Boolean(busy) || Boolean(ownKeyProblem)} onClick={generate}>
            {figure ? 'Make a new one with AI' : 'Create graphical abstract with AI'}
          </Button>
        )}
        <Button size="small" startIcon={<UploadIcon />} disabled={Boolean(busy)} onClick={() => inputRef.current?.click()}>
          {figure ? 'Replace with my own image' : 'Upload my own image'}
        </Button>
        {figure && (
          <>
            <Button size="small" startIcon={<DownloadIcon />} component="a" href={figure.url} download="graphical-abstract.webp">
              Download
            </Button>
            <Button size="small" color="error" startIcon={<RemoveIcon />} disabled={Boolean(busy)} onClick={remove}>Remove</Button>
          </>
        )}
      </Stack>
      <input ref={inputRef} type="file" hidden accept="image/png,image/jpeg,image/webp,image/tiff"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run('uploading', () => adoptUpload(f)); }} />
    </Paper>
  );
}
